import {
  useTanstackSheetsFetchFn,
  useTanstackSpotsFetchFn,
  useTanstackUserStampsFetchFn,
} from '@/components/Functional/hooks-core/useTanstackFetchFn'
import type { UserStampListResponse } from '@/schemas/v2'
import type { SpotData, SpotTagData } from '@/schemas/v2/spot'
import { useQuery } from '@tanstack/react-query'
import { useMachine } from '@xstate/react'
import { useMemo } from 'react'
import { assign, setup } from 'xstate'

// ---------------------------------------------------------------------------
// Context / Event 型
// ---------------------------------------------------------------------------

type SpotUiContext = {
  /** 選択中のタグ ID（未選択 = null） */
  selectedTagId: number | null
  /** 選択中のシート名（未選択 = ''） */
  selectedSheet: string
  /** アクティブなタブ（0 = マップ, 1 = リスト）*/
  activeTab: 0 | 1
  /** PDF ダイアログ表示中か */
  showPdfDialog: boolean
  /** ARフォトオーバーレイに表示するスポット（null = 非表示）*/
  arPhotoSpot: SpotData | null
}

type SpotUiEvent =
  /** マップタブに切り替え */
  | { type: 'SELECT_MAP' }
  /** リストタブに切り替え */
  | { type: 'SELECT_LIST' }
  /** PDF ダイアログを開く */
  | { type: 'SHOW_PDF' }
  /** PDF ダイアログを閉じる */
  | { type: 'CLOSE_PDF' }
  /** タグフィルタをトグル（同じ ID を選択すると解除） */
  | { type: 'SELECT_TAG'; tagId: number }
  /** シートフィルタを変更 */
  | { type: 'SELECT_SHEET'; sheet: string }
  /** ARフォトオーバーレイを開く */
  | { type: 'OPEN_AR_PHOTO'; spot: SpotData }
  /** ARフォトオーバーレイを閉じる */
  | { type: 'CLOSE_AR_PHOTO' }

// ---------------------------------------------------------------------------
// 内部ヘルパー
// ---------------------------------------------------------------------------

const deriveUniqueTags = (spots: SpotData[]): SpotTagData[] => {
  const seen = new Set<number>()
  const result: SpotTagData[] = []
  for (const spot of spots) {
    for (const tag of spot.tags) {
      if (!seen.has(tag.id)) {
        seen.add(tag.id)
        result.push(tag)
      }
    }
  }
  return result
}

const deriveUniqueSheets = (spots: SpotData[]): string[] => {
  const seen = new Set<string>()
  const result: string[] = []
  for (const spot of spots) {
    for (const sheet of spot.sheets) {
      if (!seen.has(sheet)) {
        seen.add(sheet)
        result.push(sheet)
      }
    }
  }
  return result
}

const applyFilter = (spots: SpotData[], selectedSheet: string, selectedTagId: number | null): SpotData[] =>
  spots.filter((spot) => {
    const matchesSheet = selectedSheet === '' || spot.sheets.includes(selectedSheet)
    const matchesTag = selectedTagId === null || spot.tags.some((t) => t.id === selectedTagId)
    return matchesSheet && matchesTag
  })

// ---------------------------------------------------------------------------
// ヘルパー: spotId → stampImage マップを構築
// ---------------------------------------------------------------------------

function buildStampImageBySpotId(stamps: UserStampListResponse['stamps']): Map<number, string> {
  const map = new Map<number, string>()
  for (const stamp of stamps) {
    if (!stamp.isAcquired || !stamp.image) continue
    for (const spot of stamp.spots) {
      // 既にエントリがある場合は先勝ちとする（複数スタンプが同一スポットを持つケースは稀）
      if (!map.has(spot.id)) {
        map.set(spot.id, stamp.image)
      }
    }
  }
  return map
}

// ---------------------------------------------------------------------------
// ヘルパー: 獲得済みスポット ID セットを構築
// buildStampImageBySpotId は stamp.image が null のスタンプを除外するため、
// ポイントラリーのように画像がないスタンプの獲得判定には利用できない。
// ---------------------------------------------------------------------------

function buildAcquiredSpotIds(stamps: UserStampListResponse['stamps']): Set<number> {
  const set = new Set<number>()
  for (const stamp of stamps) {
    if (!stamp.isAcquired) continue
    for (const spot of stamp.spots) {
      set.add(spot.id)
    }
  }
  return set
}

// ---------------------------------------------------------------------------
// UI ステートマシン（データ取得は useQuery に委譲）
// ---------------------------------------------------------------------------

const spotUiMachine = setup({
  types: {
    context: {} as SpotUiContext,
    events: {} as SpotUiEvent,
  },
}).createMachine({
  id: 'spotUi',
  context: {
    selectedTagId: null,
    selectedSheet: '',
    activeTab: 0,
    showPdfDialog: false,
    arPhotoSpot: null,
  },
  on: {
    SELECT_MAP: { actions: assign({ activeTab: 0 }) },
    SELECT_LIST: { actions: assign({ activeTab: 1 }) },
    SHOW_PDF: { actions: assign({ showPdfDialog: true }) },
    CLOSE_PDF: { actions: assign({ showPdfDialog: false }) },
    OPEN_AR_PHOTO: { actions: assign({ arPhotoSpot: ({ event }) => event.spot }) },
    CLOSE_AR_PHOTO: { actions: assign({ arPhotoSpot: null }) },
    SELECT_TAG: {
      actions: assign({
        selectedTagId: ({ context, event }) => (context.selectedTagId === event.tagId ? null : event.tagId),
      }),
    },
    SELECT_SHEET: {
      actions: assign({
        selectedSheet: ({ event }) => event.sheet,
      }),
    },
  },
})

// ---------------------------------------------------------------------------
// 公開フック
// ---------------------------------------------------------------------------

export const useSpotsFetchStateMachine = ({ isAuthenticated }: { isAuthenticated: boolean }) => {
  const spotsFetch = useTanstackSpotsFetchFn()
  const { data, isLoading } = useQuery(spotsFetch)
  const [state, send] = useMachine(spotUiMachine)

  const userStampsFetch = useTanstackUserStampsFetchFn()
  const { data: userStampsData } = useQuery({
    ...userStampsFetch,
    enabled: isAuthenticated,
  })

  // シート一覧取得（point_rally 判定用）
  const sheetsFetch = useTanstackSheetsFetchFn()
  const { data: sheetsData } = useQuery(sheetsFetch)

  const spots = data?.spots ?? []

  const stampImageBySpotId = useMemo<Map<number, string>>(
    () => (isAuthenticated && userStampsData ? buildStampImageBySpotId(userStampsData.stamps) : new Map()),
    [isAuthenticated, userStampsData],
  )

  // 獲得済みスポット ID セット（stamp.image が null でも追跡）
  const acquiredSpotIds = useMemo<Set<number>>(
    () => (isAuthenticated && userStampsData ? buildAcquiredSpotIds(userStampsData.stamps) : new Set<number>()),
    [isAuthenticated, userStampsData],
  )

  // キャンペーンに point_rally シートが 1 件以上あるか
  const isPointRally = useMemo<boolean>(
    () => (sheetsData?.sheets ?? []).some((s) => s.type === 'point_rally'),
    [sheetsData],
  )

  return {
    send,
    isLoading,
    context: {
      ...state.context,
      spots,
      uniqueTags: deriveUniqueTags(spots),
      uniqueSheets: deriveUniqueSheets(spots),
      filteredSpots: applyFilter(spots, state.context.selectedSheet, state.context.selectedTagId),
      stampImageBySpotId,
      acquiredSpotIds,
      isPointRally,
    },
  }
}
